import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Cropper, { Area, Point } from 'react-easy-crop';

import { Grid, useMediaQuery, useTheme } from '@material-ui/core';
import clsx from 'clsx';

import { Button } from '@chhjit/react-components';

import { cropImage, resizeImage } from '../../';
import { CustomSlider, useStyles } from './styles';
import { CropperProps } from './types';

export const ImageCropper = memo(
  ({
    imageForCrop,
    onCropperComplete,
    onClose,
    aspect,
    cropShape = 'round',
    maxSize = 512,
    containerAspect = 1,
    outputFormat = 'image/jpeg',
  }: CropperProps) => {
    const styles = useStyles({ containerAspect });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'), {
      noSsr: true,
    });

    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedImgAreaPixels, setCroppedImgAreaPixels] = useState<Area>();
    const [isHeightAvatarGreaterWidth, setIsHeightAvatarGreaterWidth] = useState(false);

    const image = useMemo(() => {
      return imageForCrop ? URL.createObjectURL(imageForCrop) : '';
    }, [imageForCrop]);

    const onCropChange = useCallback((location: Point) => {
      setCrop(location);
    }, []);

    const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedImgAreaPixels(croppedAreaPixels);
    }, []);

    const onCrop = useCallback(async () => {
      if (croppedImgAreaPixels) {
        const croppedImageBlob = await cropImage(
          URL.createObjectURL(imageForCrop),
          croppedImgAreaPixels,
          0,
          outputFormat,
        );
        if (croppedImageBlob) {
          const resizedCroppedImage = await resizeImage(croppedImageBlob, maxSize, maxSize);
          if (resizedCroppedImage) {
            onCropperComplete(resizedCroppedImage);
          }
        }
      }
    }, [croppedImgAreaPixels, imageForCrop, onCropperComplete, outputFormat]);

    useEffect(() => {
      if (imageForCrop) {
        const imageObj = new Image();
        imageObj.src = URL.createObjectURL(imageForCrop);
        imageObj.onload = () => {
          if (imageObj.height > imageObj.width) {
            setIsHeightAvatarGreaterWidth(true);
          }
        };
      }
    }, [imageForCrop]);

    return (
      <>
        <div className={clsx(styles.cropContainer, isHeightAvatarGreaterWidth && styles.heightAvatarGreaterWidth)}>
          <div className={styles.freeSpace} />

          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={onCropChange}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            cropShape={cropShape}
            objectFit="vertical-cover"
            initialCroppedAreaPercentages={aspect === undefined ? { x: 0, y: 0, width: 100, height: 100 } : undefined}
          />
        </div>

        <div className={styles.controls}>
          <Grid container spacing={1} justifyContent={isMobile ? 'flex-end' : 'space-between'} alignItems="center">
            <Grid item xs={isMobile ? 12 : 6}>
              <CustomSlider min={1} max={3} value={zoom} step={0.1} onChange={(_, value) => setZoom(Number(value))} />
            </Grid>

            <Grid item>
              <div>
                <Grid container justifyContent="flex-end" spacing={1}>
                  {onClose && (
                    <Grid item>
                      <Button buttonType="text" onClick={onClose} size="small">
                        Cancel
                      </Button>
                    </Grid>
                  )}
                  <Grid item>
                    <Button
                      buttonType="twoTone"
                      size="small"
                      classes={{ root: styles.cropSaveButton }}
                      onClick={() => onCrop()}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    );
  },
);
